import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './ui/table';
import { TrendingUp, Activity, RefreshCw, BarChart2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

/**
 * Feature 4 — Forecast-floor (predictive CPU/mem request pre-raise) opt-in.
 *
 * Consumes the forecast-floor management API (forecast_floor_routes.py):
 *   GET  /api/clusters/{id}/forecast-floor/status                  → this panel's data
 *   PUT  /api/clusters/{id}/forecast-floor/mode                    → cluster opt-in mode
 *   PUT  /api/clusters/{id}/forecast-floor/workloads/{ns}/{name}   → per-workload enable
 *
 * The forecast floor pre-raises a workload's CPU/mem REQUESTS ahead of a
 * predicted spike. By default every forecast-eligible workload gets it; an
 * operator can flip the cluster to opt-in-only and enable specific workloads.
 * Mirrors PredictiveHpaPanel.jsx (the Axis-B / replica equivalent). Purely
 * additive — owns its own fetch, mutates nothing else.
 */

// Merge the /chart payload into one recharts row array: 24 hourly forecast
// points plus the most recent actual p95 observed in each hour (dots).
const buildChartRows = (chart) => {
  const actualsByHour = {};
  (chart?.recent_actuals || []).forEach((a) => {
    const d = new Date(a.evaluated_at);
    const v = Number(a.actual_cpu_m);
    if (Number.isNaN(d.getTime()) || !Number.isFinite(v)) return;
    const hr = d.getHours();
    // recent_actuals is newest-first — keep the most recent value per hour.
    if (!(hr in actualsByHour)) actualsByHour[hr] = v;
  });
  return (chart?.hourly_forecast || []).map((h) => ({
    hour: `${h.hour}:00`,
    forecast_cpu_m: h.forecast_cpu_m,
    actual_cpu_m: h.hour in actualsByHour ? actualsByHour[h.hour] : null,
  }));
};

/**
 * Tiny per-row sparkline: the 24h forecast_cpu_m curve as an indigo (#6366f1)
 * line with recent actual p95 samples overlaid as teal (#0d9488) dots
 * (positioned by hour of day) — the pair passes the CVD/contrast checks on
 * both light and dark surfaces. Hand-rolled SVG (recharts inside a table cell
 * fights the layout).
 */
function FloorSparkline({ chart }) {
  if (chart === undefined) {
    return <span className="text-xs text-slate-400 dark:text-text-secondary">…</span>;
  }
  const forecast = (chart?.hourly_forecast || [])
    .map((h) => ({ hour: Number(h.hour), v: Number(h.forecast_cpu_m) }))
    .filter((p) => Number.isFinite(p.hour) && Number.isFinite(p.v));
  if (forecast.length === 0) {
    return <span className="text-xs text-slate-400 dark:text-text-secondary">—</span>;
  }
  const actuals = (chart?.recent_actuals || [])
    .map((a) => {
      const d = new Date(a.evaluated_at);
      const v = Number(a.actual_cpu_m);
      if (Number.isNaN(d.getTime()) || !Number.isFinite(v)) return null;
      return { hour: d.getHours(), v };
    })
    .filter(Boolean);

  const w = 100;
  const h = 40;
  const pad = 4;
  const values = [...forecast.map((p) => p.v), ...actuals.map((p) => p.v)];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (hour) => pad + (hour / 23) * (w - 2 * pad);
  const y = (v) => h - pad - ((v - min) / span) * (h - 2 * pad);
  const path = forecast
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.hour).toFixed(1)},${y(p.v).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Hourly forecast (indigo) vs recent actuals (teal) sparkline"
      className="overflow-visible"
    >
      <title>Hourly CPU forecast (indigo) vs recent actual p95 (teal dots)</title>
      <path
        d={path}
        fill="none"
        stroke="#6366f1"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {actuals.map((p, i) => (
        <g key={i}>
          {/* surface ring so dots separate from the forecast line */}
          <circle
            cx={x(p.hour)}
            cy={y(p.v)}
            r="3"
            fill="currentColor"
            className="text-white dark:text-zinc-900"
          />
          <circle cx={x(p.hour)} cy={y(p.v)} r="2" fill="#0d9488" />
        </g>
      ))}
    </svg>
  );
}

function AnnotationBadge({ tooltip }) {
  return (
    <Badge
      variant="outline"
      className="border-purple-300 text-purple-700 dark:text-purple-400 text-xs"
      title={tooltip}
    >
      annotation
    </Badge>
  );
}

function AxisChip({ label, active, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-2 py-0.5 rounded-full border text-xs transition-colors ${
        active
          ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
          : 'border-zinc-300 text-slate-500 dark:border-zinc-700 dark:text-text-secondary'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-emerald-400'}`}
    >
      {label}
    </button>
  );
}

/** The expanded forecast-vs-actual chart box shown under a selected workload. */
function ExpandedFloorChart({ chartLoading, chartData }) {
  return (
    <div className="mt-2 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-slate-50 dark:bg-surface-border">
      {chartLoading ? (
        <p className="text-xs text-slate-500 dark:text-text-secondary">Loading chart…</p>
      ) : chartData?.hourly_forecast?.length > 0 ? (
        <>
          <p className="text-xs font-medium text-slate-700 dark:text-white mb-2">
            CPU forecast vs actual p95 (millicores by hour)
          </p>
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart
              data={buildChartRows(chartData)}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis unit="m" tick={{ fontSize: 10 }} width={40} />
              <Tooltip formatter={(v) => [`${v}m`]} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="forecast_cpu_m"
                stroke="#6366f1"
                dot={false}
                name="Forecast"
              />
              <Scatter dataKey="actual_cpu_m" fill="#0d9488" name="Actual p95" />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-2">
            Memory raises in-place; decays via slow lane (ratchet policy).
          </p>
        </>
      ) : (
        <p className="text-xs text-slate-500 dark:text-text-secondary">
          No forecast data available for this workload yet.
        </p>
      )}
    </div>
  );
}
// Cap on how many per-row sparkline charts we prefetch per cluster.
const MAX_ROW_CHARTS = 16;

function ForecastFloorPanel({ clusterId }) {
  const [status, setStatus] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedWorkload, setSelectedWorkload] = useState(null); // {namespace, workload_name}
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newNs, setNewNs] = useState('');
  const [newName, setNewName] = useState('');
  // "ns/name" -> /chart payload (null = fetch failed / no data) for row sparklines
  const [rowCharts, setRowCharts] = useState({});
  const rowChartsRequested = useRef(new Set());

  const fetchStatus = useCallback(async () => {
    if (!clusterId) return;
    try {
      setLoading(true);
      const [statusRes, accRes] = await Promise.allSettled([
        axios.get(`${API}/clusters/${clusterId}/forecast-floor/status`),
        axios.get(`${API}/clusters/${clusterId}/forecast-floor/accuracy`),
      ]);
      setStatus(statusRes.status === 'fulfilled' ? statusRes.value.data : null);
      setAccuracy(accRes.status === 'fulfilled' ? accRes.value.data : null);
    } catch (error) {
      console.log('Forecast-floor status not available:', error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  const fetchChart = useCallback(async (namespace, workloadName) => {
    if (!clusterId) return;
    try {
      setChartLoading(true);
      const res = await axios.get(
        `${API}/clusters/${clusterId}/forecast-floor/chart/${encodeURIComponent(namespace)}/${encodeURIComponent(workloadName)}`
      );
      setChartData(res.data);
    } catch {
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Reset the sparkline cache when the cluster changes.
  useEffect(() => {
    rowChartsRequested.current = new Set();
    setRowCharts({});
  }, [clusterId]);

  // Lazily prefetch /chart for the workloads in the status table (capped) so
  // each row can render its sparkline without a click.
  useEffect(() => {
    if (!clusterId || !Array.isArray(status?.workloads)) return;
    status.workloads.slice(0, MAX_ROW_CHARTS).forEach((w) => {
      const key = `${w.namespace}/${w.workload_name}`;
      if (rowChartsRequested.current.has(key)) return;
      rowChartsRequested.current.add(key);
      axios
        .get(
          `${API}/clusters/${clusterId}/forecast-floor/chart/${encodeURIComponent(
            w.namespace
          )}/${encodeURIComponent(w.workload_name)}`
        )
        .then((res) => setRowCharts((prev) => ({ ...prev, [key]: res.data })))
        .catch(() => setRowCharts((prev) => ({ ...prev, [key]: null })));
    });
  }, [clusterId, status]);

  const openChart = (namespace, workloadName) => {
    const key = `${namespace}/${workloadName}`;
    if (selectedWorkload === key) {
      setSelectedWorkload(null);
      setChartData(null);
    } else {
      setSelectedWorkload(key);
      const cached = rowCharts[key];
      if (cached) {
        setChartData(cached);
      } else {
        fetchChart(namespace, workloadName);
      }
    }
  };

  const setMode = async (optInOnly) => {
    try {
      setSaving(true);
      await axios.put(`${API}/clusters/${clusterId}/forecast-floor/mode`, {
        opt_in_only: optInOnly,
      });
      toast.success(
        optInOnly
          ? 'Forecast floor set to opt-in only'
          : 'Forecast floor set to always-on (all eligible workloads)'
      );
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mode');
    } finally {
      setSaving(false);
    }
  };

  const setWorkload = async (namespace, workloadName, enabled) => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/clusters/${clusterId}/forecast-floor/workloads/${encodeURIComponent(
          namespace
        )}/${encodeURIComponent(workloadName)}`,
        { enabled }
      );
      toast.success(`${namespace}/${workloadName} ${enabled ? 'enabled' : 'disabled'}`);
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workload');
    } finally {
      setSaving(false);
    }
  };

  const addWorkload = async () => {
    const ns = newNs.trim();
    const name = newName.trim();
    if (!ns || !name) {
      toast.error('Enter both namespace and workload name');
      return;
    }
    await setWorkload(ns, name, true);
    setNewNs('');
    setNewName('');
  };

  // Per-row update for the Workloads table: optimistic flip of
  // enabled_effective/axes_effective, then the same PUT, then a refetch
  // (which also reverts the optimistic state if the PUT failed).
  const setWorkloadRow = async (row, { enabled, axes }) => {
    setStatus((prev) => {
      if (!prev || !Array.isArray(prev.workloads)) return prev;
      return {
        ...prev,
        workloads: prev.workloads.map((w) =>
          w.namespace === row.namespace && w.workload_name === row.workload_name
            ? {
                ...w,
                enabled_effective: enabled,
                enabled_source: 'setting',
                ...(axes !== undefined ? { axes_effective: axes } : {}),
              }
            : w
        ),
      };
    });
    try {
      setSaving(true);
      const body = axes !== undefined ? { enabled, axes } : { enabled };
      await axios.put(
        `${API}/clusters/${clusterId}/forecast-floor/workloads/${encodeURIComponent(
          row.namespace
        )}/${encodeURIComponent(row.workload_name)}`,
        body
      );
      toast.success(
        axes !== undefined
          ? `${row.namespace}/${row.workload_name} axes: ${axes.join(', ') || 'off'}`
          : `${row.namespace}/${row.workload_name} ${enabled ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workload');
    } finally {
      setSaving(false);
      await fetchStatus();
    }
  };

  const toggleAxis = (row, axis) => {
    const current = Array.isArray(row.axes_effective)
      ? row.axes_effective
      : ['cpu', 'memory'];
    const next = current.includes(axis)
      ? current.filter((a) => a !== axis)
      : [...current, axis];
    setWorkloadRow(row, { enabled: !!row.enabled_effective, axes: next });
  };

  if (!clusterId) return null;

  const optInOnly = !!status?.opt_in_only;
  // Universal predictive master switch (cluster-level). Carried on the status
  // payload; absent field (older backend) is treated as ON. When off, the panel
  // stays fully interactive (operators may pre-configure) but is visually
  // de-emphasized with a banner so the state is unmistakable.
  const masterEnabled = status?.master_enabled !== false;
  const enabledWorkloads = status?.enabled_workloads || [];
  // Per-workload effective state (annotation-aware). Absent on older backends
  // — the table simply doesn't render then and the legacy accuracy list stays.
  const workloadRows = Array.isArray(status?.workloads) ? status.workloads : null;
  const accuracyByKey = {};
  (accuracy?.workloads || []).forEach((w) => {
    accuracyByKey[`${w.namespace}/${w.workload_name}`] = w;
  });

  return (
    <Card data-testid="forecast-floor-panel" className="border border-zinc-200 dark:border-zinc-800 shadow-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Predictive Request Pre-Raise (Forecast Floor)
            </CardTitle>
            <CardDescription>
              Forecast-driven sizing raises a workload's CPU/memory <code>requests</code> ahead of a
              predicted spike, so the pod doesn't throttle or OOM at peak. Raise-only — it never
              downsizes.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            disabled={loading}
            data-testid="forecast-floor-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Master-off banner — controls stay usable so operators can pre-configure. */}
        {!masterEnabled && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
            data-testid="forecast-floor-master-off-banner"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Master switch is off — no predictive actions will be emitted. In-flight reverts still
              complete.
            </p>
          </div>
        )}

        <div className={`space-y-6 transition-opacity ${masterEnabled ? '' : 'opacity-60'}`}>
        {/* Mode toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-surface-border">
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">Opt-in only mode</p>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
              {optInOnly
                ? 'Only workloads enabled below get the forecast floor.'
                : 'Default: every forecast-eligible workload gets the floor.'}
            </p>
          </div>
          <Switch
            checked={optInOnly}
            onCheckedChange={setMode}
            disabled={saving}
            data-testid="forecast-floor-mode-toggle"
          />
        </div>

        {/* Opt-in workload management — only relevant in opt-in-only mode */}
        {optInOnly && (
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Enabled workloads
            </p>
            {enabledWorkloads.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-text-secondary mb-3">
                No workloads enabled yet — add one below to start pre-raising it.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {enabledWorkloads.map((w) => (
                  <div
                    key={`${w.namespace}/${w.workload_name}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 dark:border-zinc-800"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {w.namespace}/{w.workload_name}
                      </p>
                      {w.updated_by && (
                        <p className="text-xs text-slate-500 dark:text-text-secondary">
                          updated by {w.updated_by}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={!!w.enabled}
                      onCheckedChange={(v) => setWorkload(w.namespace, w.workload_name, v)}
                      disabled={saving}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="namespace"
                value={newNs}
                onChange={(e) => setNewNs(e.target.value)}
                className="sm:w-40"
                data-testid="forecast-floor-new-ns"
              />
              <Input
                placeholder="workload name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="sm:w-52"
                data-testid="forecast-floor-new-name"
              />
              <Button onClick={addWorkload} disabled={saving} size="sm">
                Enable workload
              </Button>
            </div>
          </div>
        )}

        {/* Per-workload effective state: toggle, axes, accuracy, drift, sparkline */}
        {workloadRows && workloadRows.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Workloads
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workload</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead>Axes</TableHead>
                  <TableHead className="text-right">Accuracy</TableHead>
                  <TableHead>Forecast vs actual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workloadRows.map((w) => {
                  const key = `${w.namespace}/${w.workload_name}`;
                  const annotationControlled =
                    w.enabled_source === 'annotation' || w.enabled_source === 'annotation_off';
                  const annotationTooltip =
                    'controlled by optops.io/predictive-requests annotation';
                  const acc = accuracyByKey[key];
                  const axes = Array.isArray(w.axes_effective)
                    ? w.axes_effective
                    : ['cpu', 'memory'];
                  return (
                    <Fragment key={key}>
                      <TableRow>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            {key}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span title={annotationControlled ? annotationTooltip : undefined}>
                              <Switch
                                checked={!!w.enabled_effective}
                                onCheckedChange={(v) =>
                                  setWorkloadRow(w, { enabled: v })
                                }
                                disabled={saving || annotationControlled}
                              />
                            </span>
                            {annotationControlled && (
                              <AnnotationBadge tooltip={annotationTooltip} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <AxisChip
                              label="cpu"
                              active={axes.includes('cpu')}
                              disabled={saving || annotationControlled}
                              onClick={() => toggleAxis(w, 'cpu')}
                            />
                            <AxisChip
                              label="memory"
                              active={axes.includes('memory')}
                              disabled={saving || annotationControlled}
                              onClick={() => toggleAxis(w, 'memory')}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="tabular-nums">
                            {acc?.accuracy_pct == null ? '—' : `${Math.round(acc.accuracy_pct)}%`}
                          </span>
                          {acc?.disabled_until && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 whitespace-nowrap">
                              paused until {new Date(acc.disabled_until).toLocaleString()}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => openChart(w.namespace, w.workload_name)}
                            title="Show full forecast chart"
                            className="cursor-pointer"
                          >
                            <FloorSparkline chart={rowCharts[key]} />
                          </button>
                        </TableCell>
                      </TableRow>
                      {selectedWorkload === key && (
                        <TableRow>
                          <TableCell colSpan={5} className="p-0 border-0">
                            <ExpandedFloorChart
                              chartLoading={chartLoading}
                              chartData={chartData}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-2">
              Memory floor is ratcheted — it decays via normal rightsizing, not the forecast loop.
            </p>
          </div>
        )}

        {/* Accuracy panel — per-workload forecast accuracy from the grader loop.
            Legacy fallback: only shown when the backend doesn't provide the
            per-workload status table above. */}
        {(!workloadRows || workloadRows.length === 0) && accuracy?.workloads?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-indigo-500" />
              Forecast accuracy (last 7 days)
            </p>
            <div className="space-y-2">
              {accuracy.workloads.map((w) => {
                const key = `${w.namespace}/${w.workload_name}`;
                const isSelected = selectedWorkload === key;
                return (
                  <div key={key}>
                    <div
                      className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-surface-border"
                      onClick={() => openChart(w.namespace, w.workload_name)}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{key}</p>
                        {w.disabled_until && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                            Drift paused until {new Date(w.disabled_until).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {w.disabled_until && (
                          <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">
                            Paused
                          </Badge>
                        )}
                        {w.accuracy_pct != null && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${w.accuracy_pct >= 70 ? 'text-emerald-600 border-emerald-400' : 'text-rose-600 border-rose-400'}`}
                          >
                            {w.accuracy_pct}% accurate
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Inline chart when workload is selected */}
                    {isSelected && (
                      <ExpandedFloorChart chartLoading={chartLoading} chartData={chartData} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ForecastFloorPanel;
